import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, Clock, ChevronRight, ArrowLeft } from "lucide-react";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import { blogPosts } from "./Blog";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 },
};

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = blogPosts.find((p) => p.slug === slug);

  if (!post) {
    return (
      <Layout>
        <img
          src="/trichy_rockfort_taxi_hero_1776155164304.png"
          alt={`taxi service in trichy - ${post?.title} travel guide trichy cab booking`}
          className="hidden"
        />
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground mb-4">Post Not Found</h1>
            <Link to="/blog" className="btn-primary">Back to Blog</Link>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <SEOHead
        title={`${post.title} | Best Trichy to Velankanni Taxi`}
        description={`${post.excerpt} Book reliable Trichy to Velankanni taxi for local and outstation travel.`}
        keywords={`trichy to velankanni taxi, taxi service in trichy, trichy taxi service, best cab service in trichy, ${post.title.toLowerCase()}, trichy travel guide, taxi booking trichy`}
      />

      <section className="pt-28 pb-8 section-padding bg-gradient-to-b from-primary/5 to-transparent">
        <div className="container-custom max-w-3xl">
          <motion.div {...fadeUp} className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
            <Link to="/" className="hover:text-primary">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/blog" className="hover:text-primary">Blog</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-primary truncate">{post.title}</span>
          </motion.div>
          <motion.h1 {...fadeUp} className="text-3xl sm:text-4xl font-bold text-foreground">
            {post.title}
          </motion.h1>
          <motion.div {...fadeUp} className="flex items-center gap-4 text-sm text-muted-foreground mt-4">
            <span className="flex items-center gap-1"><Calendar className="w-4 h-4" /> {post.date}</span>
            <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {post.readTime}</span>
          </motion.div>
        </div>
      </section>

      <section className="section-padding pt-8">
        <div className="container-custom max-w-3xl">
          <motion.article {...fadeUp} className="prose prose-invert prose-sm max-w-none text-muted-foreground">
            {post.content.split("\n\n").map((paragraph, i) => {
              if (paragraph.startsWith("**") && paragraph.endsWith("**")) {
                return <h3 key={i} className="text-xl font-semibold text-foreground mt-8 mb-3">{paragraph.replace(/\*\*/g, "")}</h3>;
              }
              const formatted = paragraph.replace(/\*\*(.*?)\*\*/g, '<strong class="text-foreground">$1</strong>');
              return <p key={i} className="mb-4 leading-relaxed" dangerouslySetInnerHTML={{ __html: formatted }} />;
            })}
          </motion.article>

          <div className="mt-12 pt-8 border-t border-border">
            <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-primary font-medium hover:gap-3 transition-all">
              <ArrowLeft className="w-4 h-4" /> Back to Blog
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default BlogPost;
